import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '@/routes/AuthConfig';
import useGeoLocation from '@/hooks/useGeoLocation';

type IdTokenClaims = {
  [key: string]: unknown;
  email: string;
};

interface UserContextType {
  email?: IdTokenClaims['email'] | string;
  userId?: string;
  isUserLoading: boolean;
  isAuthenticated?: boolean;
  geoLocation?: {
    country_3: string;
    ip: string;
    country: string;
    name: string;
  };
}

const UserContext = createContext<UserContextType | undefined>(undefined);

/**
 * UserProvider component that wraps parts of the app that need access to user information.
 *
 * This component utilizes the MSAL hook to retrieve account details,
 * sets the user state based on the available account information,
 * and then provides this state to child components in the tree.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - The child components that will consume the user context.
 * @returns {JSX.Element} The provider component wrapping its children.
 */
export const UserProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const { accounts, inProgress, instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState<UserContextType>({ isUserLoading: true, isAuthenticated });
  const { location: geoLocation, loading: geoLoading, error: geoError } = useGeoLocation();

  useEffect(() => {
    const updateAuthState = async () => {
      setUser((prevUser) => {
        let newState = { ...prevUser, isAuthenticated };

        if (accounts.length > 0) {
          const currentAccount = accounts[0];

          if (!currentAccount.idTokenClaims && inProgress === InteractionStatus.None) {
            // We need to refresh token, this will be handled in a separate async call
            instance
              .acquireTokenSilent({
                ...loginRequest,
                account: currentAccount,
              })
              .then((response) => {
                setUser((latest) => ({
                  ...latest,
                  email: (response.idTokenClaims as IdTokenClaims).email?.toString() || currentAccount.username,
                  userId: currentAccount.localAccountId,
                }));
              })
              .catch((error) => {
                console.error('Failed to acquire token silently:', error);
                setUser((latest) => ({
                  ...latest,
                  email: currentAccount.username || undefined,
                  userId: currentAccount.localAccountId,
                }));
              });

            return newState;
          } else {
            // We already have the claims, update synchronously
            newState.email = currentAccount.idTokenClaims?.email?.toString() || currentAccount.username;
            newState.userId = currentAccount.localAccountId;
          }
        }

        return newState;
      });
    };

    updateAuthState();
  }, [accounts, isAuthenticated, inProgress, instance]);

  useEffect(() => {
    const updateGeoLocationState = () => {
      if (!geoLoading) {
        setUser((prevUser) => {
          const newState = { ...prevUser };

          if (prevUser.isUserLoading) {
            newState.isUserLoading = false;
          }

          if (geoLocation) {
            newState.geoLocation = geoLocation;
          } else if (geoError) {
            console.warn('Error fetching geolocation:', geoError);
          }
          return newState;
        });
      }
    };

    updateGeoLocationState();
  }, [geoLocation, geoLoading, geoError]);

  return <UserContext.Provider value={user}>{children}</UserContext.Provider>;
};

/**
 * Custom hook to access the user context.
 *
 * This hook must be used within a UserProvider. If not, it will throw an error.
 */
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
